import { proxyScope } from './index';

/**
 * set proxy inherit from proxyProtoType, make sure
 * Array.isArray(proxy) === true
 * proxy.__proto__ === proxyProtoType
 * @param {Array} proxy
 * @param {Object} proxyProtoType
 */
export function proxyArray(proxy, proxyProtoType) {
  if (Object.setPrototypeOf) {
    Object.setPrototypeOf(proxy, proxyProtoType);
  } else if ('__proto__' in {}) {
    proxy.__proto__ = proxyProtoType; // eslint-disable-line no-proto
  }
}

/**
 * check if state is a form
 * @param {Atom} state
 */
export function isStateForm(state) {
  return state && state.getValue('createForm');
}

/**
 * proxy a state, make sure
 * 1. for array: push, splice slice, shift, unshift call state method
 * 2. for array: other methods adapter to state's data
 * 3. for object, it has a clear method, which actually calls state.clear
 * 4. make sure JSON.stringify() returns correct
 * @param {Atom} state
 */
function getProxy(state) {
  let proxy = {};
  let proxyProto = {};
  if (state.type === 'LIST') {
    proxy = [];
    proxyProto = Object.create(state.getData());
    [
      'push',
      'splice',
      'slice',
      'shift',
      'unshift',
    ].forEach((prop) => {
      proxyProto[prop] = state[prop].bind(state);
    });
    proxyArray(proxy, proxyProto);
  } else if (state.clear) {
    proxyProto.clear = state.clear.bind(state);
    proxy = Object.create(proxyProto);
  }
  proxyProto.toJSON = () => state.getData();
  return proxy;
}

/**
 * proxy a common state tree, make sure every props and props' attribute
 * is observable
 * In the state hierarchy, there could be a new state is observable
 * in this case, proxyScope will be called to generate new proxy object which
 * could have more APIs
 * @param {Atom} atom the atom which is observable
 * @param {Atom} currentState atom.data.maps[key], use to find children atom, which could be used to proxyScope
 * @param {String} prop top prop name which will be changed if some value is set
 * @param {Object} root top value which is used when set value
 * @param {Object} target current value, it's root or some child attribute of root
 * @param {Context} ctx current ctx, which is used to proxyScope
 */
export function proxyTree(atom, currentState, prop, root, target, ctx) {
  if (!target || typeof target !== 'object') {
    return target;
  }
  const proxy = getProxy(currentState);
  Object.keys(target).forEach((key) => {
    Object.defineProperty(proxy, key, {
      get() {
        const hookPoint = currentState.data.maps || currentState.data.items;
        const child = hookPoint[key];
        if (child && child.componentSchema) {
          return proxyScope({
            ctx,
            scope: currentState,
            prop: key,
          });
        }
        return proxyTree(atom, hookPoint[key], prop,
          root, target[key], ctx);
      },
      set(value) {
        target[key] = value;
        atom.set(prop, root);
      },
      enumerable: true,
    });
  });
  return proxy;
}

/**
 * proxy a value, in case it's a tree, if any part of it is changed, we need
 * to set current state, value and other props have different  implementation.
 * 'value' is not a prop of atoms, it's under fieldData/value.
 * @param {Object} target current value, it's root or some child attribute of root
 * @param {Atom} atom the atom which is observable
 * @param {Object} root top value which is used when set value
 */
export function proxyValueTree(target, atom, root) {
  if (!target || typeof target !== 'object') {
    return target;
  }
  let proxy = {};
  let proxyProto = {};
  const properties = {};
  if (Array.isArray(target)) {
    proxy = [];
    const arrayProto = Array.prototype;
    proxyProto = Object.create(arrayProto);
    proxyArray(proxy, proxyProto);
    [
      'push',
      'pop',
      'shift',
      'unshift',
      'splice',
      'sort',
      'reverse',
    ].forEach((prop) => {
      proxyProto[prop] = (...arg) => {
        const result = target[prop](...arg);
        // no form could reach here, just use setVal
        atom.setVal(root);
        return result;
      };
    });
    ['join'].forEach((prop) => {
      proxyProto[prop] = (...arg) => target[prop](...arg);
    });
  } else {
    proxy = Object.create(proxyProto);
  }
  proxyProto.toJSON = () => target;
  Object.keys(target).forEach((prop) => {
    properties[prop] = {
      get() {
        return proxyValueTree(target[prop], atom, root);
      },
      set(value) {
        target[prop] = value;
        // no form could reach here, just use setVal
        atom.setVal(root);
      },
      enumerable: true,
    };
  });
  Object.defineProperties(proxy, properties);
  return proxy;
}

export function proxyBehavior(proxy, state, form) {
  let obj = state;
  if (isStateForm(state)) {
    obj = form;
  }
  const data = state.getValue();
  if (data && ('behavior' in data)) {
    Object.defineProperties(proxy, {
      behavior: {
        get() {
          return state.getBehavior();
        },
        set(value) {
          if (isStateForm(state)) {
            form.setBehavior(value);
          }
          if (state.setBehavior) {
            state.setBehavior(value);
          }
        },
        enumerable: true,
      },
      visible: {
        get() {
          return state.getBehavior && state.getBehavior() === 'NORMAL';
        },
        set(value) {
          return obj.setBehavior && obj.setBehavior(value ? 'NORMAL' : 'HIDDEN');
        },
        enumerable: true,
      },
      disabled: {
        get() {
          return state.getBehavior && state.getBehavior() === 'DISABLED';
        },
        set(value) {
          return obj.setBehavior && obj.setBehavior(value ? 'DISABLED' : 'NORMAL');
        },
        enumerable: true,
      },
      hidden: {
        get() {
          return state.getBehavior && state.getBehavior() === 'HIDDEN';
        },
        set(value) {
          return obj.setBehavior && obj.setBehavior(value ? 'HIDDEN' : 'NORMAL');
        },
        enumerable: true,
      },
      readonly: {
        get() {
          return state.getBehavior && state.getBehavior() === 'READONLY';
        },
        set(value) {
          return obj.setBehavior && obj.setBehavior(value ? 'READONLY' : 'NORMAL');
        },
        enumerable: true,
      },
    });
  }
}
export function proxyStateProps(proxy, state, context) {
  if (state.data && state.data.maps) {
    Object.keys(state.data.maps).forEach((prop) => {
      let point = proxy;
      if (prop in proxy) {
        if (!proxy.props) {
          proxy.props = {};
        }
        point = proxy.props;
      }
      if (prop !== 'behavior') {
        Object.defineProperty(point, prop, {
          get() {
            return proxyTree(state, state.data.maps[prop], prop,
              state.getData(prop), state.getData(prop), context);
          },
          set(value) {
            state.set(prop, value);
          },
          // fix issue for Object.keys
          enumerable: true,
        });
      }
    });
  }
}

const behaviorKeys = {
  hidden: 'HIDDEN',
  readonly: 'READONLY',
  disabled: 'DISABLED',
};

function getBehaviorValue(key, value, state) {
  if (state.get(key) !== undefined) {
    return undefined;
  }
  if (key === 'visible') {
    return value ? 'NORMAL' : 'HIDDEN';
  }
  if (!value) {
    return 'NORMAL';
  }
  return behaviorKeys[key];
}

/**
 *
 * @param {form | undefined} state 要改的值的state或form
 * @param {any} value 要改成改的值
 */
export function setValue(obj, value, state) {
  if (typeof value === 'object') {
    // break into props
    Object.keys(value).forEach((key) => {
      const behave = getBehaviorValue(key, value[key], state);
      if (key === 'value') {
        if (obj.setValue) {
          obj.setValue(value[key]);
        } else if ('fieldData' in obj.getValue()) {
          obj.setVal(value[key]);
        } else {
          obj.setData(value);
        }
      } else if (key === 'behavior') {
        obj.setBehavior(value[key]);
      } else if (behave) {
        obj.setBehavior(behave);
      } else { // 忽略
        state.set(key, value[key]);
      }
    });
  }
}
